import React from "react"
import { Col, Div, Label, Row, Switch, Text } from "atomize"
import { motion } from "framer-motion"
import { theme } from "../settings/config"
import Container from "../atoms/Container"
import Paragraph from "../atoms/Paragraph"
import TextInput from "../inputs/TextInput"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import {
	getLessonColor,
	isExactPlayerCount,
	lessonTypeHasCourt,
	lessonTypeDisplayName,
	lessonTypeHasPlayers,
	lessonTypeNamesCanWait,
	lessonTypeTakesShortName,
	PICKABLE_LESSON_TYPE_COLORS,
} from "../utils/func.utils"
import RadioBox from "../atoms/RadioBox"
import { SUBSTITUTE_PAID_TO_COACH, SUBSTITUTE_PAID_TO_SCHOOL } from "../utils/billing.utils"
import { isSubscriptionCourse } from "../utils/substitutes.utils"

// Scrivere una tipologia è una cosa in sé, non una riga in più in fondo al
// listino: sta su una schermata sua, con Indietro che riporta al listino
// esattamente com'era. Incastrata tra i prezzi si finiva a compilare un
// modulo dentro un modulo, senza sapere cosa salvasse cosa.
//
// Qui non si salva niente: si compila. La tipologia entra nella bozza del
// listino, e va su Firestore quando si salva il listino — insieme al suo
// prezzo, che è la ragione per cui la si sta scrivendo.
//
// La stessa schermata serve anche a correggere una tipologia che c'è già, e
// lì cambia quanto si può toccare. Una tipologia del circolo è sua e si
// cambia tutta. Una del catalogo no: come si chiama e cos'è è uguale per
// tutti, e riscriverlo in un circolo vorrebbe dire due app che chiamano la
// stessa lezione in due modi. Quello che è davvero di questo circolo — in
// quanti si sta, se i nomi si segnano, se si occupa un campo, se si fa in più
// maestri — si cambia.

// Una tipologia nuova nasce vuota. Il minimo parte da uno perché una lezione
// con zero allievi non è una lezione, e il massimo si può non dire — "da 3
// allievi" è legittimo, ed è come è fatto il SAT.
//
// La tinta di chi non ne aveva una è quella che la lezione prendeva
// comunque: partire da lì non sposta niente di quello che si vedeva prima.
export const draftFromLessonType = lessonType => ({
	lessonTypeName: lessonType?.lessonTypeName ?? "",
	lessonTypeDescription: lessonType?.lessonTypeDescription ?? "",
	minimumPlayers: String(lessonType?.minimumPlayers ?? 1),
	maxPlayers: lessonType?.maxPlayers ? String(lessonType.maxPlayers) : "",
	// Il giro normale è quello coi nomi e col campo: chi fa altro lo dice.
	needsPlayers: lessonTypeHasPlayers(lessonType),
	needsCourt: lessonTypeHasCourt(lessonType),
	// Più istruttori in campo: il SAT sì, il resto finché il circolo non lo
	// dice.
	allowsAssistants: lessonType?.allowsAssistants === true,
	color: lessonType?.color ?? "ric",
	// Quanto dura di solito. Sul documento sta in ore, perché è in ore che si
	// somma all'ora di inizio; qui si scrive in minuti, perché "90" si digita
	// e "1,5" a seconda di dove sei è un numero o non lo è.
	durationMinutes: lessonType?.defaultDuration
		? String(Math.round(lessonType.defaultDuration * 60))
		: "",
	// Nei corsi, chi incassa chi viene al posto di un abbonato. Dove non è
	// detto è il circolo.
	substitutePaidTo: lessonType?.substitutePaidTo ?? SUBSTITUTE_PAID_TO_SCHOOL,
	// Nei corsi, la sigla al posto del nome: spenta finché il circolo non la
	// vuole, e pronta con quella del catalogo, che è quella di sempre.
	useShortName: lessonType?.useShortName === true,
	shortName: lessonType?.shortName ?? lessonType?.lessonTypeName ?? "",
})

export const EMPTY_DRAFT_TYPE = draftFromLessonType(null)

// Una lezione più corta di così non è una lezione, e una più lunga di una
// giornata di lavoro nemmeno. Il tetto è lo stesso che controlla il server,
// perché una durata rifiutata al salvataggio è una durata da non far
// scrivere.
const MIN_DURATION_MINUTES = 15
const MAX_DURATION_MINUTES = 8 * 60

// Le private sono il loro numero: vedi `isExactPlayerCount`, che sta con le
// altre regole delle tipologie perché la usa anche la lezione.
export { isExactPlayerCount }

// La bozza di una tipologia del circolo è una privata quando minimo e massimo
// scritti coincidono: lì gli assistenti non si propongono.
const draftIsExactPlayerCount = draftType =>
	draftType.needsPlayers &&
	draftType.maxPlayers !== "" &&
	Number(draftType.minimumPlayers) === Number(draftType.maxPlayers)

// Cosa manca per poterla creare. Il massimo, se c'è, non può stare sotto il
// minimo: sarebbe una tipologia che nessun numero di allievi può soddisfare.
export const draftTypeIsValid = draftType => {
	if (draftType.lessonTypeName.trim() === "") return false
	// una sigla accesa e vuota non si leggerebbe
	if (draftType.useShortName && draftType.shortName.trim() === "") return false

	// La durata si può non dire — vuol dire un'ora — ma se la si dice deve
	// essere un numero di minuti vero. Vale per tutte: anche un centro estivo
	// che i nomi non li segna comincia a un'ora e finisce a un'altra.
	if (draftType.durationMinutes !== "") {
		const minutes = Number(draftType.durationMinutes)

		if (!Number.isInteger(minutes)) return false
		if (minutes < MIN_DURATION_MINUTES || minutes > MAX_DURATION_MINUTES) return false
	}

	// una lezione senza nomi non ha un numero di allievi da validare
	if (!draftType.needsPlayers) return true

	const min = Number(draftType.minimumPlayers)
	const max = Number(draftType.maxPlayers)

	if (!Number.isInteger(min) || min < 1) return false
	if (draftType.maxPlayers !== "" && (!Number.isInteger(max) || max < min)) return false

	return true
}

// La bozza come la vuole Firestore: i numeri sono numeri, il massimo che non
// c'è non c'è, e una lezione senza nomi non porta con sé un numero di allievi
// che nessuno le chiederà mai.
export const buildSchoolLessonType = draftType => {
	const named = {
		lessonTypeName: draftType.lessonTypeName.trim(),
		lessonTypeDescription: draftType.lessonTypeDescription.trim(),
		color: draftType.color,
		...(draftType.needsCourt ? {} : { needsCourt: false }),
		// spento non si scrive, e in una privata non si scrive mai
		...(draftType.allowsAssistants && !draftIsExactPlayerCount(draftType)
			? { allowsAssistants: true }
			: {}),
		// una durata che non c'è non si scrive: un'ora è già il ripiego
		...(draftType.durationMinutes === ""
			? {}
			: { defaultDuration: Number(draftType.durationMinutes) / 60 }),
	}

	if (!draftType.needsPlayers) return { ...named, needsPlayers: false }

	const minimumPlayers = Number(draftType.minimumPlayers)
	const maxPlayers = Number(draftType.maxPlayers)

	return {
		...named,
		minimumPlayers,
		...(maxPlayers >= minimumPlayers ? { maxPlayers } : {}),
	}
}

// ──────────────────────────────────────────────────────────────────────────
// UNA CORREZIONE È SOLO LA DIFFERENZA
//
// Di una tipologia del catalogo il circolo si scrive quello che qui è
// diverso, e niente altro: così il catalogo resta padrone del resto — se
// domani gli si aggiunge una descrizione, quella arriva anche a chi la
// correzione ce l'ha già.
//
// Gli allievi vanno insieme, minimo e massimo: sono un intervallo, e mezzo
// intervallo scritto qui e mezzo nel catalogo sarebbe un intervallo che
// nessuno dei due ha mai controllato. Il massimo che non c'è si scrive
// `null`, perché "nessun massimo" è una risposta e va detta: non dire
// niente vorrebbe dire tenersi quello del catalogo.
//
// Se non è diverso niente non si scrive niente, e la correzione sparisce:
// la tipologia torna quella di tutti.
// ──────────────────────────────────────────────────────────────────────────
export const buildLessonTypeAdjustment = (draftType, catalogType) => {
	const adjustment = {}

	// SAT e CAD i giocatori li hanno sempre: i nomi si possono rimandare, ma
	// spegnerli del tutto ne farebbe una lezione senza allievi, e finirebbe
	// nella riga in cima accanto al centro estivo.
	if (
		!lessonTypeNamesCanWait(catalogType) &&
		draftType.needsPlayers !== lessonTypeHasPlayers(catalogType)
	) {
		adjustment.needsPlayers = draftType.needsPlayers
	}
	if (draftType.needsCourt !== lessonTypeHasCourt(catalogType)) {
		adjustment.needsCourt = draftType.needsCourt
	}
	// a una privata l'interruttore non si propone: niente da correggere
	if (
		!isExactPlayerCount(catalogType) &&
		draftType.allowsAssistants !== (catalogType?.allowsAssistants === true)
	) {
		adjustment.allowsAssistants = draftType.allowsAssistants
	}

	if (draftType.needsPlayers) {
		const minimumPlayers = Number(draftType.minimumPlayers)
		const maxPlayers = draftType.maxPlayers === "" ? null : Number(draftType.maxPlayers)

		if (
			minimumPlayers !== catalogType?.minimumPlayers ||
			maxPlayers !== (catalogType?.maxPlayers ?? null)
		) {
			adjustment.minimumPlayers = minimumPlayers
			adjustment.maxPlayers = maxPlayers
		}
	}

	if (
		draftType.substitutePaidTo !==
		(catalogType?.substitutePaidTo ?? SUBSTITUTE_PAID_TO_SCHOOL)
	) {
		adjustment.substitutePaidTo = draftType.substitutePaidTo
	}

	// La sigla si scrive solo accesa: spenta, il corso si chiama col suo nome
	// come per tutti, e non c'è niente da correggere.
	if (draftType.useShortName) {
		adjustment.useShortName = true
		adjustment.shortName = draftType.shortName.trim()
	}

	return Object.keys(adjustment).length > 0 ? adjustment : null
}

function LessonTypeForm({
	lessonTypeID,
	draftType,
	catalogType,
	isOwn,
	isNew,
	onChange,
	onBack,
	onAdd,
}) {
	const setDraft = patch => onChange({ ...draftType, ...patch })

	// Del catalogo si correggono i numeri, non l'identità: il nome, cos'è e
	// la tinta si vedono, così si sa di cosa si sta parlando, ma non si
	// toccano da qui.
	const playersAreLocked = !isOwn && isExactPlayerCount(catalogType)

	// SAT e CAD si segnano sempre coi giocatori — i nomi, semmai, dopo: la
	// domanda non si fa.
	const needsPlayersIsLocked = !isOwn && lessonTypeNamesCanWait(catalogType)

	// Una privata ha un maestro solo: la domanda non si fa.
	const assistantsAreOffered = isOwn
		? !draftIsExactPlayerCount(draftType)
		: !isExactPlayerCount(catalogType)

	const countBox = (value, onValueChange) => (
		<Div w="4.5rem" style={{ flexShrink: 0 }}>
			<TextInput
				type="number"
				value={value}
				m="0"
				h="2.75rem"
				textAlign="center"
				placeholder="—"
				handleChange={onValueChange}
			/>
		</Div>
	)

	// Le tinte sono quelle che il tema tiene per le lezioni: si sceglie tra
	// quelle e non si inventa, o una schermata di lezioni non si riconosce
	// più. Il pallino mostra la tinta chiara, che è quella che si vedrà in
	// elenco, col bordo scuro della stessa coppia.
	const colorPicker = () => (
		<Div d="flex" flexWrap="wrap" m={{ t: "0.5rem" }} style={{ gap: "0.6rem" }}>
			{PICKABLE_LESSON_TYPE_COLORS.map(colorKey => {
				const isSelected = draftType.color === colorKey

				return (
					<motion.div key={colorKey} whileTap={{ scale: 0.9 }}>
						<Div
							w="2.2rem"
							h="2.2rem"
							rounded="circle"
							cursor="pointer"
							transition
							bg={getLessonColor({ color: colorKey }, false)}
							border="3px solid"
							borderColor={
								isSelected
									? getLessonColor({ color: colorKey }, true)
									: theme.colors.gray_light
							}
							onClick={() => setDraft({ color: colorKey })}
						/>
					</motion.div>
				)
			})}
		</Div>
	)

	// Gli interruttori chiedono cose diverse, e vanno chieste separate:
	// c'è chi segna i nomi senza occupare un campo — una lezione di teoria —
	// e chi occupa un campo senza segnare i nomi, come il live ball.
	const toggle = (title, hint, field) => (
		<Label
			onClick={() => setDraft({ [field]: !draftType[field] })}
			display="flex"
			justify="space-between"
			align="center"
			m={{ y: "1rem" }}>
			<Div style={{ flex: 1, minWidth: 0 }}>
				<Text textSize="body" textWeight="bold">
					{title}
				</Text>
				<Text textSize="caption" textColor={theme.colors.text_light}>
					{hint}
				</Text>
			</Div>
			<Div style={{ flexShrink: 0 }}>
				<Switch
					m={{ l: "0.5rem" }}
					shadow="0"
					checked={draftType[field]}
					activeColor="gunmetal_fill"
					inactiveColor="gray_light"
				/>
			</Div>
		</Label>
	)

	const playersRange = () => (
		<>
			<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
				<Text textSize="body" textColor={theme.colors.text_light}>
					da
				</Text>
				{countBox(draftType.minimumPlayers, e => setDraft({ minimumPlayers: e.target.value }))}
				<Text textSize="body" textColor={theme.colors.text_light}>
					a
				</Text>
				{countBox(draftType.maxPlayers, e => setDraft({ maxPlayers: e.target.value }))}
			</Div>
			<Text
				textSize="caption"
				textColor={theme.colors.text_light}
				m={{ t: "0.5rem", b: "1rem" }}>
				Il massimo si può lasciare vuoto: vuol dire "da {draftType.minimumPlayers || "1"} in
				su".
			</Text>
		</>
	)

	// Quanto dura non dipende dai nomi né dal campo: un centro estivo senza
	// elenco dei presenti comincia a un'ora e finisce a un'altra come tutto
	// il resto. Sta prima degli allievi perché quelli vanno e vengono con
	// l'interruttore, e un campo che salta su e giù si perde di vista.
	const duration = () => (
		<>
			<Text textSize="body" textWeight="bold" m={{ b: "0.5rem" }}>
				Quanto dura
			</Text>
			<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
				{countBox(draftType.durationMinutes, e =>
					setDraft({ durationMinutes: e.target.value }),
				)}
				<Text textSize="body" textColor={theme.colors.text_light}>
					minuti
				</Text>
			</Div>
			<Text
				textSize="caption"
				textColor={theme.colors.text_light}
				m={{ t: "0.5rem", b: "1rem" }}>
				Vuota vuol dire un'ora. È solo quello che ti propone come orario di fine: la
				singola lezione la allunghi e l'accorci come sempre.
			</Text>
		</>
	)

	const lockedPlayers = () => (
		<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
			Sempre {catalogType.minimumPlayers}: è quel numero a fare la tipologia, e cambiarlo
			cambierebbe il senso a tutte le lezioni già segnate così.
		</Text>
	)

	// Nei corsi viene anche chi l'abbonamento non ce l'ha, al posto di un
	// assente, e paga la volta: a chi, lo decide il circolo.
	const substitutePaidTo = () => (
		<>
			<Text textSize="body" textWeight="bold" m={{ b: "0.25rem" }}>
				Chi incassa i sostituti
			</Text>
			<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.75rem" }}>
				Chi viene al posto di un abbonato e paga la lezione. Se incassa il maestro, nel
				riepilogo trova quanto dare al circolo a fine mese.
			</Text>
			<RadioBox
				text="Il circolo"
				checked={draftType.substitutePaidTo === SUBSTITUTE_PAID_TO_SCHOOL}
				handleChange={() => setDraft({ substitutePaidTo: SUBSTITUTE_PAID_TO_SCHOOL })}
			/>
			<RadioBox
				text="Il maestro, che poi li dà al circolo"
				checked={draftType.substitutePaidTo === SUBSTITUTE_PAID_TO_COACH}
				handleChange={() => setDraft({ substitutePaidTo: SUBSTITUTE_PAID_TO_COACH })}
			/>
		</>
	)

	// del catalogo il nome che si legge, non la sigla: "Corso adulti"
	const title = isNew
		? "Una tipologia del circolo"
		: isOwn
			? draftType.lessonTypeName
			: lessonTypeDisplayName(lessonTypeID, { [lessonTypeID]: catalogType })

	// Una sigla al posto del nome, per il circolo che ci è abituato: vale
	// solo qui, e Calendar continua a scrivere quella del catalogo.
	const shortName = () => (
		<>
			{toggle(
				"Usa una sigla",
				"Acceso, nelle lezioni del circolo si legge la sigla al posto del nome.",
				"useShortName",
			)}
			{draftType.useShortName && (
				<Div d="flex" align="center" style={{ gap: "0.75rem" }} m={{ t: "-0.5rem" }}>
					<Div w="8.5rem" style={{ flexShrink: 0 }}>
						<TextInput
							value={draftType.shortName}
							placeholder="es. CAD"
							handleChange={e => setDraft({ shortName: e.target.value.slice(0, 8) })}
						/>
					</Div>
					<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.5rem" }}>
						Il calendario e gli altri circoli non cambiano.
					</Text>
				</Div>
			)}
		</>
	)

	const subtitle = isNew
		? "Quello che qui si fa e che il listino non prevede. La vedranno tutti i maestri del " +
			"circolo; il prezzo glielo metti dopo, tornando indietro."
		: isOwn
			? "È una tipologia del circolo: la vedono tutti i maestri che insegnano qui, e quello " +
				"che cambi lo vedono anche loro."
			: "È una tipologia di tutti: come si chiama e cos'è non si cambia da qui. Quanti " +
				"allievi ci stanno e come si svolge invece è cosa vostra."

	return (
		<Container>
			<Div m={{ b: "0.25rem" }} textWeight="bold" textSize="title">
				{title}
			</Div>
			<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
				{subtitle}
			</Text>

			<Div maxH="55vh" overflow="auto">
				<Container>
					{isOwn ? (
						<>
							<Paragraph title="Come si chiama">
								<TextInput
									value={draftType.lessonTypeName}
									placeholder="es. Live ball"
									handleChange={e => setDraft({ lessonTypeName: e.target.value })}
								/>
							</Paragraph>
							<Paragraph title="Cos'è (facoltativo)">
								<TextInput
									value={draftType.lessonTypeDescription}
									placeholder="es. partite a punteggio, palle continue"
									handleChange={e =>
										setDraft({ lessonTypeDescription: e.target.value })
									}
								/>
							</Paragraph>

							<Text textSize="body" textWeight="bold" m={{ t: "0.5rem" }}>
								Di che colore
							</Text>
							<Text textSize="caption" textColor={theme.colors.text_light}>
								È come la riconoscerai in elenco.
							</Text>
							{colorPicker()}
						</>
					) : (
						draftType.lessonTypeDescription && (
							<Text
								textSize="body"
								textColor={theme.colors.text_light}
								m={{ b: "0.5rem" }}>
								{draftType.lessonTypeDescription}
							</Text>
						)
					)}

					{!isOwn && lessonTypeTakesShortName(lessonTypeID) && shortName()}

					{!needsPlayersIsLocked &&
						toggle(
							"Si segnano i giocatori",
							"Spento, non chiede chi c'era né quanto paga.",
							"needsPlayers",
						)}

					{toggle(
						"Si occupa un campo",
						"Spento, non chiede il campo e non entra nelle ore dei campi.",
						"needsCourt",
					)}

					{assistantsAreOffered &&
						toggle(
							"Più istruttori in campo",
							"Acceso, nella lezione si possono aggiungere colleghi come assistenti.",
							"allowsAssistants",
						)}

					{/* La durata la si scrive solo sulle proprie: di una
					    tipologia di tutti è il catalogo a dire quanto dura,
					    come il nome. */}
					{isOwn && duration()}

					{/* Il numero di allievi non è un dettaglio del listino: è
					    quello che fa scegliere questa tipologia da sola quando
					    si scrive una lezione con tanti giocatori. */}
					{draftType.needsPlayers && (
						<>
							<Text textSize="body" textWeight="bold" m={{ b: "0.5rem" }}>
								Quanti allievi
							</Text>
							{playersAreLocked ? lockedPlayers() : playersRange()}
						</>
					)}

					{isSubscriptionCourse(lessonTypeID) && substitutePaidTo()}
				</Container>
			</Div>

			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							textSize="subheader"
							text="Indietro"
							action={onBack}
						/>
					</Col>
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={theme.colors.gunmetal_fill}
							textColor="white"
							textSize="subheader"
							text={isNew ? "Aggiungi" : "Fatto"}
							disabled={!draftTypeIsValid(draftType)}
							action={onAdd}
						/>
					</Col>
				</Row>
			</Div>
		</Container>
	)
}

export default LessonTypeForm
